import { supabase } from '../../../shared/lib/supabase';

export interface MinutesUsageByCategory {
  category: string;
  label: string;
  seconds: number;
  minutes: number;
  percentage: number;
  color: string;
}

export interface MinutesUsageSummary {
  totalSeconds: number;
  totalMinutes: number;
  categories: MinutesUsageByCategory[];
}

/** Per-match minutes for billing (split by 8v8 vs 11v11). matchId null = unallocated (e.g. master files not linked to a match). */
export interface MinutesUsageMatchBreakdownItem {
  matchId: string | null;
  minutes: number;
}

export type MinutesUsageSummaryWithBreakdown = MinutesUsageSummary & {
  matchBreakdown: MinutesUsageMatchBreakdownItem[];
};

/**
 * Service to calculate minutes usage by organization
 * 
 * Note: Minutes calculation for highlights uses the same filtering logic
 * as the match assets screen (highlightsTacticsService):
 * - Tournament access AND Team access (both required)
 * - Only matches in accessible tournaments that also have at least one accessible team
 * - Only completed/stitched highlights are counted
 * 
 * This ensures consistency between what orgs see in match assets and what's counted in minutes usage.
 */
export const minutesUsageService = {
  /**
   * Get minutes usage breakdown for an organization
   * 
   * Calculates minutes for:
   * 1. Video Library Master Files - Direct from videos table (duration_seconds)
   * 2. Highlights - Only for matches in accessible tournaments AND with accessible teams
   *    Duration calculated from segments_data: sum of (endTime - startTime) for each segment
   * 
   * @param orgId - Organization ID
   * @param dateFrom - Optional start date for filtering (ISO string)
   * @param dateTo - Optional end date for filtering (ISO string)
   */
  getMinutesUsageByOrg: async (
    orgId: string,
    dateFrom?: string | null,
    dateTo?: string | null
  ): Promise<MinutesUsageSummary> => {
    try {
      // 1. Video Library Master Files (videos table - only R2/storage videos)
      let videosQuery = supabase
        .from('videos')
        .select('id, duration_seconds')
        .eq('org_id', orgId)
        .eq('is_active', true)
        .in('source_type', ['cloudflare_r2', 'supabase_storage', 'local']);
      
      // Apply date filter if provided (filter by created_at for consistency with other entities)
      if (dateFrom) {
        videosQuery = videosQuery.gte('created_at', dateFrom);
      }
      if (dateTo) {
        // Add one day to include the entire end date
        const endDate = new Date(dateTo);
        endDate.setDate(endDate.getDate() + 1);
        videosQuery = videosQuery.lt('created_at', endDate.toISOString());
      }
      
      const { data: videos, error: videosError } = await videosQuery;

      if (videosError) {
        console.error('[MinutesUsage] Error fetching videos:', videosError);
      }

      const videoMasterSeconds = videos?.reduce((sum, video) => {
        return sum + (video.duration_seconds || 0);
      }, 0) || 0;

      console.log('[MinutesUsage] Video Library Master Files:', {
        videoCount: videos?.length || 0,
        totalSeconds: videoMasterSeconds,
        totalMinutes: (videoMasterSeconds / 60).toFixed(2),
      });

      // Get accessible tournament and team IDs for the org (same logic as storage usage)
      const { data: tournamentAccess, error: tournamentError } = await supabase
        .from('tournament_access')
        .select('tournament_id')
        .eq('organisation_id', orgId)
        .eq('is_active', true);

      if (tournamentError) {
        console.error('[MinutesUsage] Error fetching tournament access:', tournamentError);
      }

      const tournamentIdsArray = tournamentAccess?.map(ta => ta.tournament_id) || [];
      console.log('[MinutesUsage] Tournament access:', { count: tournamentIdsArray.length });

      // 2. Highlights (video_highlights table)
      // Use the same filtering logic as storage usage:
      // - Tournament access AND Team access (both required)
      // - Only matches in accessible tournaments that also have at least one accessible team
      // Duration calculated from segments_data JSONB column
      let highlightSeconds = 0;
      
      console.log('[MinutesUsage] Calculating highlights minutes for org:', orgId);

      // Step 1: Get accessible team IDs for the org
      const { data: teamAccess, error: teamAccessError } = await supabase
        .from('team_access')
        .select('team_id')
        .eq('organisation_id', orgId)
        .eq('active', true);

      if (teamAccessError) {
        console.error('[MinutesUsage] Error fetching team access:', teamAccessError);
      }

      const accessibleTeamIds = teamAccess?.map(ta => ta.team_id) || [];
      console.log('[MinutesUsage] Team access:', { count: accessibleTeamIds.length });

      // Step 2: Get accessible matches using AND logic (tournament AND team access)
      let accessibleMatchIds: string[] = [];

      if (tournamentIdsArray.length > 0 && accessibleTeamIds.length > 0) {
        // Step 2a: Get matches from accessible tournaments only
        const { data: matches, error: matchesError } = await supabase
          .from('matches')
          .select('id, tournament_id, home_team_id, away_team_id')
          .in('tournament_id', tournamentIdsArray)
          .eq('active', true);

        if (matchesError) {
          console.error('[MinutesUsage] Error fetching matches:', matchesError);
        } else if (matches && matches.length > 0) {
          // Step 2b: Filter matches by team access (home or away team must be accessible)
          const accessibleMatches = matches.filter(
            (match: any) =>
              accessibleTeamIds.includes(match.home_team_id) ||
              accessibleTeamIds.includes(match.away_team_id)
          );

          accessibleMatchIds = accessibleMatches.map((m: any) => m.id);
          console.log('[MinutesUsage] Accessible matches (tournament AND team access):', {
            totalMatchesInTournaments: matches.length,
            accessibleMatchesCount: accessibleMatchIds.length,
          });
        }
      } else {
        console.log('[MinutesUsage] No tournament or team access - no highlights accessible');
      }

      // Step 3: Get completed and stitched highlights for accessible matches only
      // Need to fetch segments_data to calculate duration
      if (accessibleMatchIds.length > 0) {
        let highlightsQuery = supabase
          .from('video_highlights')
          .select('id, match_id, segments_data, created_at')
          .in('match_id', accessibleMatchIds)
          .in('status', ['stitched', 'completed'])
          .eq('is_active', true);
        
        // Apply date filter if provided (filter by created_at)
        if (dateFrom) {
          highlightsQuery = highlightsQuery.gte('created_at', dateFrom);
        }
        if (dateTo) {
          const endDate = new Date(dateTo);
          endDate.setDate(endDate.getDate() + 1);
          highlightsQuery = highlightsQuery.lt('created_at', endDate.toISOString());
        }
        
        const { data: accessibleHighlights, error: highlightsError } = await highlightsQuery;

        if (highlightsError) {
          console.error('[MinutesUsage] Error fetching highlights:', highlightsError);
        } else if (accessibleHighlights && accessibleHighlights.length > 0) {
          // Step 4: Calculate duration from segments_data
          // segments_data is a JSONB array of EditorClip objects
          // Each segment has startTime and endTime in seconds
          accessibleHighlights.forEach((highlight: any) => {
            try {
              let segmentsData = highlight.segments_data;
              
              // Parse if it's a string
              if (typeof segmentsData === 'string') {
                segmentsData = JSON.parse(segmentsData);
              }
              
              // Calculate duration for this highlight
              if (Array.isArray(segmentsData) && segmentsData.length > 0) {
                const highlightDuration = segmentsData.reduce((sum: number, segment: any) => {
                  const startTime = segment.startTime || 0;
                  const endTime = segment.endTime || 0;
                  const segmentDuration = Math.max(0, endTime - startTime);
                  return sum + segmentDuration;
                }, 0);
                
                highlightSeconds += highlightDuration;
              }
            } catch (error) {
              console.warn('[MinutesUsage] Error parsing segments_data for highlight:', highlight.id, error);
            }
          });

          const highlightsWithSegments = accessibleHighlights.filter((h: any) => {
            try {
              let segmentsData = h.segments_data;
              if (typeof segmentsData === 'string') {
                segmentsData = JSON.parse(segmentsData);
              }
              return Array.isArray(segmentsData) && segmentsData.length > 0;
            } catch {
              return false;
            }
          });

          console.log('[MinutesUsage] Highlights minutes calculation:', {
            accessibleHighlights: accessibleHighlights.length,
            highlightsWithSegments: highlightsWithSegments.length,
            totalSeconds: highlightSeconds,
            totalMinutes: (highlightSeconds / 60).toFixed(2),
          });
        } else {
          console.log('[MinutesUsage] No highlights found for accessible matches');
        }
      } else {
        console.log('[MinutesUsage] No accessible matches - no highlights to calculate');
      }

      // Calculate totals
      const totalSeconds = videoMasterSeconds + highlightSeconds;
      const totalMinutes = totalSeconds / 60;

      // Final summary log
      console.log('[MinutesUsage] Final summary:', {
        videoMasterSeconds,
        highlightSeconds,
        totalSeconds,
        totalMinutes: totalMinutes.toFixed(2),
      });

      // Calculate percentages
      // Show 2 categories: Video Library Master Files and Highlights
      const categories: MinutesUsageByCategory[] = [
        {
          category: 'videos',
          label: 'Video Library Master Files',
          seconds: videoMasterSeconds,
          minutes: videoMasterSeconds / 60,
          percentage: totalSeconds > 0 ? (videoMasterSeconds / totalSeconds) * 100 : 0,
          color: '#3B82F6', // blue
        },
        {
          category: 'highlights',
          label: 'Highlights',
          seconds: highlightSeconds,
          minutes: highlightSeconds / 60,
          percentage: totalSeconds > 0 ? (highlightSeconds / totalSeconds) * 100 : 0,
          color: '#F59E0B', // amber
        },
      ];

      return {
        totalSeconds,
        totalMinutes,
        categories,
      };
    } catch (error) {
      console.error('[MinutesUsage] Error calculating minutes usage:', error);
      return {
        totalSeconds: 0,
        totalMinutes: 0,
        categories: [],
      };
    }
  },

  /**
   * Get minutes usage with per-match breakdown for billing (8v8 vs 11v11 split).
   * Same totals as getMinutesUsageByOrg plus matchBreakdown: minutes per match (matchId null = unallocated).
   */
  getMinutesUsageByOrgWithMatchBreakdown: async (
    orgId: string,
    dateFrom?: string | null,
    dateTo?: string | null
  ): Promise<MinutesUsageSummaryWithBreakdown> => {
    const summary = await minutesUsageService.getMinutesUsageByOrg(orgId, dateFrom, dateTo);
    const matchSeconds = new Map<string | null, number>();

    try {
      // 1. Video Library: attribute duration to match via match_videos (storage_video_id = videos.id)
      let videosQuery = supabase
        .from('videos')
        .select('id, duration_seconds')
        .eq('org_id', orgId)
        .eq('is_active', true)
        .in('source_type', ['cloudflare_r2', 'supabase_storage', 'local']);
      if (dateFrom) videosQuery = videosQuery.gte('created_at', dateFrom);
      if (dateTo) {
        const endDate = new Date(dateTo);
        endDate.setDate(endDate.getDate() + 1);
        videosQuery = videosQuery.lt('created_at', endDate.toISOString());
      }
      const { data: videos } = await videosQuery;
      if (videos && videos.length > 0) {
        const videoIds = videos.map((v: any) => v.id);
        const { data: links } = await supabase
          .from('match_videos')
          .select('match_id, storage_video_id')
          .in('storage_video_id', videoIds);
        const videoToMatch = new Map<string, string>();
        (links || []).forEach((row: any) => {
          if (row.storage_video_id && row.match_id) videoToMatch.set(row.storage_video_id, row.match_id);
        });
        videos.forEach((video: any) => {
          const matchId = videoToMatch.get(video.id) ?? null;
          const sec = Number(video.duration_seconds || 0);
          matchSeconds.set(matchId, (matchSeconds.get(matchId) ?? 0) + sec);
        });
      }

      // 2. Highlights: accessible matches and per-highlight duration by match_id
      const { data: tournamentAccess } = await supabase
        .from('tournament_access')
        .select('tournament_id')
        .eq('organisation_id', orgId)
        .eq('is_active', true);
      const { data: teamAccess } = await supabase
        .from('team_access')
        .select('team_id')
        .eq('organisation_id', orgId)
        .eq('active', true);
      const tournamentIds = tournamentAccess?.map((ta: any) => ta.tournament_id) || [];
      const teamIds = teamAccess?.map((ta: any) => ta.team_id) || [];
      let accessibleMatchIds: string[] = [];
      if (tournamentIds.length > 0 && teamIds.length > 0) {
        const { data: matches } = await supabase
          .from('matches')
          .select('id, home_team_id, away_team_id')
          .in('tournament_id', tournamentIds)
          .eq('active', true);
        const accessible = (matches || []).filter(
          (m: any) => teamIds.includes(m.home_team_id) || teamIds.includes(m.away_team_id)
        );
        accessibleMatchIds = accessible.map((m: any) => m.id);
      }
      if (accessibleMatchIds.length > 0) {
        let highlightsQuery = supabase
          .from('video_highlights')
          .select('id, match_id, segments_data, created_at')
          .in('match_id', accessibleMatchIds)
          .in('status', ['stitched', 'completed'])
          .eq('is_active', true);
        if (dateFrom) highlightsQuery = highlightsQuery.gte('created_at', dateFrom);
        if (dateTo) {
          const endDate = new Date(dateTo);
          endDate.setDate(endDate.getDate() + 1);
          highlightsQuery = highlightsQuery.lt('created_at', endDate.toISOString());
        }
        const { data: highlights } = await highlightsQuery;
        (highlights || []).forEach((h: any) => {
          try {
            let segmentsData = h.segments_data;
            if (typeof segmentsData === 'string') segmentsData = JSON.parse(segmentsData);
            if (!Array.isArray(segmentsData) || segmentsData.length === 0) return;
            const sec = segmentsData.reduce((sum: number, seg: any) => {
              const s = seg.startTime || 0;
              const e = seg.endTime || 0;
              return sum + Math.max(0, e - s);
            }, 0);
            const matchId = h.match_id || null;
            matchSeconds.set(matchId, (matchSeconds.get(matchId) ?? 0) + sec);
          } catch {
            // skip
          }
        });
      }

      const matchBreakdown: MinutesUsageMatchBreakdownItem[] = Array.from(matchSeconds.entries())
        .filter(([, sec]) => sec > 0)
        .map(([matchId, sec]) => ({ matchId, minutes: Math.round((sec / 60) * 10000) / 10000 }));

      return { ...summary, matchBreakdown };
    } catch (e) {
      console.warn('[MinutesUsage] Match breakdown failed, returning summary only:', e);
      return { ...summary, matchBreakdown: [] };
    }
  },
};
